/**
 * Content Controls for Word document metadata (Phase 5c.2).
 *
 * Inserts/reads Content Controls with `kobsign:` tag prefix.
 * These let users embed metadata fields directly in the document,
 * which are read back automatically when sending/archiving.
 *
 * Uses RichText CCs (WordApi 1.1) for broad compatibility.
 * Enhanced properties (appearance, cannotDelete) applied when supported.
 */

import { useState, useCallback } from 'react'
import { devLog } from '../services/devLog'
import type { DocumentMetadata } from '../components/metadata/MetadataPanel'

const KOBSIGN_PREFIX = 'kobsign:'

/** Definition for a content control field */
export interface ContentControlField {
  tag: string
  titleKey: string           // i18n key for title (visible label in Word)
  placeholderKey: string     // i18n key for placeholder
}

/** Predefined field definitions (tags only — titles/placeholders come from i18n) */
export const CC_FIELD_TAGS = {
  counterparty: 'kobsign:counterparty',
  document_type: 'kobsign:document_type',
  contract_value: 'kobsign:contract_value',
  contract_currency: 'kobsign:contract_currency',
  contract_expires_at: 'kobsign:contract_expires_at',
  department: 'kobsign:department',
  responsible_person: 'kobsign:responsible_person',
} as const

/** KobSign brand color for CC borders */
const CC_COLOR = '#0052CC'

/** Insert a content control at the current cursor position */
export async function insertContentControl(
  tag: string,
  title: string,
  placeholderText: string,
): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection()
    const cc = selection.insertContentControl()

    cc.tag = tag
    cc.title = title
    cc.placeholderText = placeholderText

    // Enhanced properties — applied best-effort (may require 1.3+)
    try {
      cc.color = CC_COLOR
    } catch { /* color not supported */ }

    try {
      cc.appearance = Word.ContentControlAppearance.boundingBox
    } catch { /* appearance requires WordApi 1.3+ */ }

    try {
      cc.cannotDelete = true
    } catch { /* cannotDelete not supported */ }

    await context.sync()
    devLog.info('CC', `Inserted "${tag}" — "${title}"`)
  })
}

/** Read all kobsign: content controls from the document */
export async function readContentControls(): Promise<Record<string, string>> {
  return await Word.run(async (context) => {
    const controls = context.document.body.contentControls
    controls.load('items/tag,items/text,items/placeholderText')
    await context.sync()

    const values: Record<string, string> = {}
    for (const cc of controls.items) {
      if (cc.tag?.startsWith(KOBSIGN_PREFIX)) {
        const text = cc.text?.trim()
        // Only capture non-empty values (ignore placeholder text)
        if (text && text !== cc.placeholderText) {
          values[cc.tag] = text
        }
      }
    }

    devLog.info('CC', `Read ${Object.keys(values).length} field(s) from document`)
    return values
  })
}

/** Convert raw CC tag→value map to DocumentMetadata */
export function ccValuesToMetadata(ccValues: Record<string, string>): Partial<DocumentMetadata> {
  const metadata: Partial<DocumentMetadata> = {}

  const cp = ccValues[CC_FIELD_TAGS.counterparty]
  if (cp) {
    const parts = cp.split(',').map((s) => s.trim()).filter(Boolean)
    if (parts.length > 0) metadata.counterparties = parts
  }

  if (ccValues[CC_FIELD_TAGS.contract_value]) {
    metadata.contract_value = ccValues[CC_FIELD_TAGS.contract_value]
  }
  if (ccValues[CC_FIELD_TAGS.contract_currency]) {
    metadata.contract_currency = ccValues[CC_FIELD_TAGS.contract_currency]
  }
  if (ccValues[CC_FIELD_TAGS.contract_expires_at]) {
    metadata.contract_expires_at = ccValues[CC_FIELD_TAGS.contract_expires_at]
  }

  // Note: document_type, department, responsible_person are IDs in backend
  // but text in CCs. These are passed as text — backend/panel may need to resolve.

  return metadata
}

/**
 * Merge metadata from Content Controls with metadata from the task pane panel.
 * CC values take priority (user wrote them directly in the document).
 */
export function mergeMetadata(
  panelMetadata: DocumentMetadata,
  ccMetadata: Partial<DocumentMetadata>,
): DocumentMetadata {
  return {
    ...panelMetadata,
    // CC overrides panel where CC has values
    ...(ccMetadata.counterparties && ccMetadata.counterparties.length > 0 && {
      counterparties: ccMetadata.counterparties,
    }),
    ...(ccMetadata.contract_value != null && {
      contract_value: ccMetadata.contract_value,
    }),
    ...(ccMetadata.contract_currency != null && {
      contract_currency: ccMetadata.contract_currency,
    }),
    ...(ccMetadata.contract_expires_at != null && {
      contract_expires_at: ccMetadata.contract_expires_at,
    }),
  }
}

/** Remove all kobsign: content controls from the document (keeps text) */
export async function removeAllContentControls(): Promise<number> {
  return await Word.run(async (context) => {
    const controls = context.document.body.contentControls
    controls.load('items/tag')
    await context.sync()

    let count = 0
    for (const cc of controls.items) {
      if (cc.tag?.startsWith(KOBSIGN_PREFIX)) {
        try {
          cc.cannotDelete = false
        } catch { /* ignore */ }
        cc.delete(true) // true = keep the text content
        count++
      }
    }
    await context.sync()

    devLog.info('CC', `Removed ${count} content control(s), text preserved`)
    return count
  })
}

/** React hook wrapping Content Controls operations */
export function useContentControls() {
  const [isInserting, setIsInserting] = useState(false)
  const [isReading, setIsReading] = useState(false)

  const insertField = useCallback(async (tag: string, title: string, placeholder: string) => {
    setIsInserting(true)
    try {
      await insertContentControl(tag, title, placeholder)
    } finally {
      setIsInserting(false)
    }
  }, [])

  const readFields = useCallback(async (): Promise<Record<string, string>> => {
    setIsReading(true)
    try {
      return await readContentControls()
    } finally {
      setIsReading(false)
    }
  }, [])

  const readAsMetadata = useCallback(async (): Promise<Partial<DocumentMetadata>> => {
    const raw = await readFields()
    return ccValuesToMetadata(raw)
  }, [readFields])

  return {
    insertField,
    readFields,
    readAsMetadata,
    removeAll: removeAllContentControls,
    isInserting,
    isReading,
  }
}
